import { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  faCalendar,
  faFilePdf,
  faList,
  faNewspaper,
  faThumbTack,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import searchApi from '@/core/search/api/search-api';
import {
  SearchPagination,
  SearchQueryFilters,
  SearchQueryFilterType,
  SearchResponse,
  SearchResultItem,
} from '@/core/search/model/search';
import { useDebounce } from '@/shared/hooks/export';
import { authService, useSessionId } from '@/core/auth/export';
import { analyticsService, AnalyticsEvent, AnalyticsTypes } from '@/shared/analytics/export';

export const searchQueryFilterTypes: SearchQueryFilterType[] = [
  'newsletter',
  'bullet',
  'headline',
  'story',
  'article',
  'event',
  'genre',
  'pdf',
];
export type SearchFilterType = 'bullet' | 'article' | 'event' | 'genre' | 'pdf';
export const searchFiltersTypes: SearchFilterType[] = [
  'article',
  'bullet',
  'event',
  'genre',
  'pdf',
];

export interface SearchPreview {
  type: SearchFilterType;
  id: string;
}

export const SearchFiltersTypeToIcon = new Map<SearchQueryFilterType, JSX.Element>([
  [
    'article',
    <FontAwesomeIcon size="xs" icon={faNewspaper} style={{ color: 'var(--bs-primary)' }} />,
  ],
  ['bullet', <FontAwesomeIcon size="xs" icon={faList} style={{ color: 'var(--bs-primary)' }} />],
  ['event', <FontAwesomeIcon size="xs" icon={faCalendar} style={{ color: 'var(--bs-primary)' }} />],
  [
    'genre',
    <FontAwesomeIcon size="xs" icon={faThumbTack} style={{ color: 'var(--bs-primary)' }} />,
  ],
  ['pdf', <FontAwesomeIcon size="xs" icon={faFilePdf} style={{ color: 'var(--bs-primary)' }} />],
]);

export enum SearchFiltersTypeToColor {
  article = 'pills-orange',
  bullet = 'secondary',
  event = 'success',
  genre = 'warning',
  pdf = 'danger',
}

interface SearchContextType {
  search: string;
  searchRef: React.RefObject<HTMLDivElement>;
  modalRef: React.MutableRefObject<HTMLDivElement | null>;
  loading: boolean;
  setSearch: (value: string) => void;
  pagination: SearchPagination;
  setPagination: (pagination: SearchPagination) => void;
  filters: SearchQueryFilters;
  setFilters: (filters: SearchQueryFilters) => void;
  results: SearchResultItem[];
  searchResults: () => Promise<void>;
  /** Whether a request for the next page would return more results. */
  hasMore: boolean;
  /** Appends the next page to `results`. */
  loadMore: () => void;
  hideSearchResults: boolean;
  setHideSearchResults: (hide: boolean) => void;
  /** The result shown by its type's `Preview` (see search-result-type-registry). */
  previewed: SearchPreview | undefined;
  setPreviewed: (previewed: SearchPreview | undefined) => void;
  getHostSpecificFilters: () => SearchQueryFilters;
  filterType?: SearchFilterType;
}

export const SearchContext = createContext<SearchContextType | undefined>(undefined);

export function SearchProvider({
  children,
  filterType,
}: {
  children: React.ReactNode;
  filterType?: SearchFilterType;
}) {
  const sessionId = useSessionId();
  const subject = authService.getSubjectData();

  const getHostSpecificFilters = useCallback((): SearchQueryFilters => {
    if (filterType) {
      return {
        search: '',
        types: filterType === 'article' ? ['newsletter', 'story', 'headline'] : [filterType],
        sortBy: 'date:desc',
      };
    }

    return {
      search: '',
      types: ['newsletter', 'bullet', 'headline', 'story', 'event', 'genre', 'pdf'],
      sortBy: 'date:desc',
    };
  }, [filterType]);

  const [search, setSearch] = useState('');
  const searchRef = useRef(null);
  const modalRef = useRef(null);
  const [hideSearchResults, setHideSearchResults] = useState(true);
  const debouncedSearch = useDebounce(search, 500);
  const [pagination, setPagination] = useState<SearchPagination>({ page: 1, pageSize: 3 });
  const [filters, setFilters] = useState<SearchQueryFilters>(getHostSpecificFilters());
  const [results, setResults] = useState<SearchResultItem[]>([]);
  const [loadedPage, setLoadedPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const requestIdRef = useRef(0);
  const [previewed, setPreviewed] = useState<SearchPreview>();

  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const sendDebouncedSearchEvent = (searchValue: string, results: SearchResultItem[]) => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);

    debounceTimerRef.current = setTimeout(() => {
      analyticsService.recordAnalyticsEvent(AnalyticsEvent.SEARCH_COMMAND, {
        subject,
        object: {
          type: AnalyticsTypes.SEARCH,
          config: {
            search: searchValue,
            results: results.map(r => ({
              uri: r.uri,
              title: r.title,
            })),
          },
        },
        session: {
          id: sessionId,
        },
      });
    }, 5000); // 5 seconds
  };

  const fetchPage = useCallback(
    async (page: number) => {
      // Responses can arrive out of order; only the latest request may update state
      const requestId = ++requestIdRef.current;
      setLoading(true);

      // Article is a special case
      const articleTypes: SearchQueryFilterType[] = ['story', 'newsletter', 'headline'];
      const hasAllArticleTypes = articleTypes.every(t => filters.types?.includes(t));
      const types: any[] = filters.types?.filter(f => !articleTypes.includes(f)) || [];
      if (hasAllArticleTypes) types.push(articleTypes);

      // One failed bucket (e.g. pdf) must not drop the others nor leave the search loading.
      // A failed bucket also counts as having no more pages, so the sentinel stops retrying it
      const settled = await Promise.allSettled(
        types.map(
          async t =>
            await searchApi.search({
              pagination: { ...pagination, page },
              filters: {
                ...filters,
                types: typeof t === 'string' ? [t] : t,
                search: debouncedSearch,
              },
            }),
        ),
      );
      if (requestId !== requestIdRef.current) return;

      const response: (SearchResponse | undefined)[] = settled.map((outcome, index) => {
        if (outcome.status === 'fulfilled') return outcome.value;
        console.warn('Search request failed for type', types[index], outcome.reason);
        return undefined;
      });
      setLoading(false);

      let pageResults: SearchResultItem[] = [];
      response.forEach(r => (pageResults = pageResults.concat(r?.results || [])));
      setResults(prev => {
        if (page === 1) return pageResults;
        // New documents shift the following pages, so a page can repeat earlier results
        const loaded = new Set(prev.map(r => r.uri));
        return prev.concat(pageResults.filter(r => !loaded.has(r.uri)));
      });
      setLoadedPage(page);
      setHasMore(
        response.some(r => !!r?.pagination && r.pagination.page < (r.pagination.pageCount ?? 0)),
      );

      if (page === 1 && debouncedSearch && debouncedSearch.length > 2) {
        sendDebouncedSearchEvent(debouncedSearch, pageResults);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pagination, filters, debouncedSearch],
  );

  const searchResults = useCallback(() => fetchPage(1), [fetchPage]);

  const loadMore = useCallback(() => {
    if (!loading && hasMore) fetchPage(loadedPage + 1);
  }, [fetchPage, loading, hasMore, loadedPage]);

  useEffect(() => {
    searchResults();
  }, [searchResults]);

  useEffect(() => {
    // matchAll is the user's choice, not a host default, so it survives the reset
    if (debouncedSearch === '')
      setFilters(prev => ({ ...getHostSpecificFilters(), matchAll: prev.matchAll }));
  }, [debouncedSearch, getHostSpecificFilters]);

  interface ToggleSearchResultsDetail {
    open: boolean;
  }

  const toggleSearchResults = useMemo(
    () =>
      new CustomEvent<ToggleSearchResultsDetail>('toggleSearchResults', {
        detail: { open: !hideSearchResults },
      }),
    [hideSearchResults],
  );

  useEffect(() => {
    const handleToggleSearchResults = (e: CustomEvent<ToggleSearchResultsDetail>) => {
      if (e.detail.open) {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = '';
      }
    };

    document.addEventListener('toggleSearchResults', handleToggleSearchResults as EventListener);

    return () => {
      document.removeEventListener(
        'toggleSearchResults',
        handleToggleSearchResults as EventListener,
      );
    };
  }, []);

  useEffect(() => {
    document.dispatchEvent(toggleSearchResults);
  }, [hideSearchResults, toggleSearchResults]);

  return (
    <SearchContext.Provider
      value={{
        search,
        searchRef,
        modalRef,
        loading,
        setSearch,
        pagination,
        setPagination,
        filters,
        setFilters,
        results,
        searchResults,
        hasMore,
        loadMore,
        hideSearchResults,
        setHideSearchResults,
        previewed,
        setPreviewed,
        getHostSpecificFilters,
        filterType,
      }}
    >
      {children}
    </SearchContext.Provider>
  );
}
